import { useLingui } from '@lingui/react/macro';

import type { ChatPreviewKind } from '#modules/chat/utils/systemMessage';
import type { LastSystemActor } from '../model/roomSessions';

/**
 * Слова для служебной строки в превью комнаты – одни на пилюлю, окно и полосу.
 *
 * Жили тремя копиями словаря в трёх поверхностях, и копии уже расходились порядком ключей.
 * Хук, а не функция с `t` в параметре: макрос превращает шаблон в обращение к каталогу только
 * в том модуле, где импортирован, – поэтому `useLingui` вызывается здесь же.
 *
 * Подлежащее – когда оно известно: «Вы вступили в сессию» себе, «Юра зашёл в голос» про других.
 * Без него превью остаётся безличным, как было.
 */
export const useRoomSystemPhrase = () => {
  const { t } = useLingui();

  /*
    `selfInVoice` – в голосе ли этой сессии я сам прямо сейчас. Строки «вышел из голоса» сервер
    не пишет намеренно (лента не должна мигать переподключениями), и после выхода превью
    оставалось «Вы зашли в голос» рядом с «Никого в голосе» и кнопкой входа – человек читал
    это как «я уже в голосе» (15.09.2026).

    `voiceUserIds` – кто в голосе этой сессии прямо сейчас, по живому составу. С 22.09.2026 то же
    правило действует и про других: в пилюле лиц нет, и строка «Юра зашёл в голос» висела рядом с
    «0 в голосе» часами - проверить глазами было нельзя, только развернув окно. `null` значит «не
    знаем»: тогда говорим как раньше, в настоящем, потому что соврать молчанием хуже.
  */
  return (
    kind: ChatPreviewKind,
    systemActor: LastSystemActor | null = null,
    selfInVoice: boolean | null = null,
    voiceUserIds: ReadonlySet<string> | null = null
  ): string => {
    const actor = systemActor?.name ?? null;

    switch (kind) {
      case 'deleted':
        return t`Message deleted`;
      case 'sessionCreated':
        return t`Session created`;
      case 'sessionFinished':
        return t`Session finished`;
      case 'sessionCancelled':
        return t`Session cancelled`;
      case 'postDeleted':
        return t`Listing deleted`;
      /*
        «Давайте не здесь»: последним ходом был обмен контактами мимо Teamplay. В ленте на это
        стоит плашка, превью хватает короткой строки - той же, что в списке переписок.

        Ветки здесь не было вовсе: словарь молча возвращал undefined, а компилятор этого не
        замечал, пока ветка голоса не стала блоком.
      */
      case 'offPlatform':
        return t`Taking it off Teamplay`;
      case 'memberJoined':
        if (systemActor?.isSelf) {
          return t`post:session.room.previewSelfJoined`;
        }
        return actor ? t`${actor} joined the session` : t`A player joined`;
      case 'memberLeft':
        // О себе - целой фразой: «Вы» в шаблоне с именем дало бы «Вы вышел из сессии».
        if (systemActor?.isSelf) {
          return t`post:session.room.previewSelfLeft`;
        }
        return actor ? t`${actor} left the session` : t`A player left`;
      case 'voiceJoined': {
        if (systemActor?.isSelf) {
          return selfInVoice === false
            ? t`post:session.room.previewSelfWasInVoice`
            : t`post:session.room.previewSelfJoinedVoice`;
        }
        if (!actor) {
          return t`A player joined voice`;
        }
        /*
          Он всё ещё там? Знаем состав и его в нём нет - говорим в прошедшем. Состав неизвестен
          (комната свёрнута и снимка нет) - оставляем как было: «не знаю» это не «ушёл».
        */
        const stillThere = systemActor?.userId ? voiceUserIds?.has(systemActor.userId) : null;
        return stillThere === false ? t`${actor} was in voice` : t`${actor} joined voice`;
      }
    }
  };
};
